<script setup lang="ts">
import type { FilePreview } from '@/services/sharepoint-drive/sharepoint-drive-service';
import { computed, onMounted, ref, useId, watch } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();

const props = defineProps<{
  preview: FilePreview;
}>();

// Adapted from the CESG-785 prototype's SharePointPreviewFrame.vue WOPI-style
// post-into-named-iframe mechanism. Unique per component instance (rather than that prototype's
// fixed constant) so multiple previews open at once — not a current file-manager scenario, but
// not ruled out either — don't collide on the same form/iframe target.
const frameName = `file-preview-frame-${useId()}`;

const formRef = ref<HTMLFormElement | null>(null);

// preview.postParameters is a URL-encoded query string (e.g. "X-UA-Compatible=IE%3Dedge&..."),
// one hidden <input> per key/value pair.
const postParameters = computed<[string, string][]>(() => {
  if (!props.preview.postUrl || !props.preview.postParameters) return [];
  return Array.from(new URLSearchParams(props.preview.postParameters).entries());
});

function submitForm() {
  formRef.value?.submit();
}

onMounted(submitForm);
watch(() => props.preview, submitForm);
</script>

<template>
  <div class="file-preview-frame">
    <form
      v-if="preview.postUrl"
      ref="formRef"
      method="post"
      :action="preview.postUrl"
      :target="frameName"
      hidden
    >
      <input
        v-for="[key, value] in postParameters"
        :key="key"
        type="hidden"
        :name="key"
        :value="value"
      />
    </form>
    <iframe
      :name="frameName"
      class="file-preview-iframe"
      :src="preview.postUrl ? undefined : preview.getUrl"
      :title="t('fileManager.previewModal.filePreviewFrame.iframeTitle')"
    ></iframe>
  </div>
</template>

<style scoped>
.file-preview-frame {
  width: 100%;
  height: 100%;
}

.file-preview-iframe {
  width: 100%;
  height: 100%;
  border: none;
}
</style>
